(() => {
  if (window.ZalvaroSizeGuideInitialized) return;
  window.ZalvaroSizeGuideInitialized = true;

  const STORAGE_UNIT = 'zalvaro:size-unit';

  const CM_PER_INCH = 2.54;

  /* Leather does not give and US customers layer a sweatshirt underneath, so the
     finished jacket has to clear the body by this much. The allowances are the
     workshop's, stated in inches; they are held in centimetres here because the
     pattern table is metric, but the arithmetic is identical either way. */
  const CHEST_EASE_INCH = 3.5;
  const WAIST_EASE_INCH = 2.75;
  const CHEST_EASE = CHEST_EASE_INCH * CM_PER_INCH; /* 8.89 cm */
  const WAIST_EASE = WAIST_EASE_INCH * CM_PER_INCH; /* 6.985 cm */

  /* Float guard, so a size that clears the target exactly is never rejected. */
  const EPSILON = 1e-6;

  /* Plausible body chest / waist range, in centimetres. */
  const MIN_CM = 70;
  const MAX_CM = 190;

  const returnFocus = new WeakMap();
  const calcState = new WeakMap();

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const getFocusableElements = (dialog) =>
    Array.from(dialog.querySelectorAll(focusableSelector)).filter(
      (element) => !element.closest('[hidden]') && element.getClientRects().length > 0
    );

  const readStored = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeStored = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Private mode or blocked storage — preferences simply do not persist. */
    }
  };

  const restorePage = (dialog) => {
    if (!document.querySelector('[data-z-size-guide][open]')) {
      document.documentElement.classList.remove('z-size-guide-open');
    }

    const trigger = returnFocus.get(dialog);
    returnFocus.delete(dialog);
    if (trigger?.isConnected) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }
  };

  const closeGuide = (dialog) => {
    if (!dialog || !dialog.hasAttribute('open')) return;

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      restorePage(dialog);
    }
  };

  const toNumber = (value) => {
    const number = Number.parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(number) ? number : null;
  };

  const formatNumber = (value, unit) => {
    const number = toNumber(value);
    if (number === null) return String(value);

    if (unit === 'in') return (number / 2.54).toFixed(1).replace(/\.0$/, '');
    return Number.isInteger(number) ? String(number) : number.toFixed(1).replace(/\.0$/, '');
  };

  const formatMeasurement = (value, unit) => {
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return value;

    if (unit === 'in') return (number / 2.54).toFixed(1);
    return Number.isInteger(number) ? String(number) : number.toFixed(1).replace(/\.0$/, '');
  };

  const getUnit = (dialog) => (dialog.dataset.unit === 'in' ? 'in' : 'cm');

  const getStrings = (dialog) => {
    const cached = dialog.__zSizeStrings;
    if (cached) return cached;

    let parsed = {};
    const holder = dialog.querySelector('[data-z-size-strings]');
    if (holder) {
      try {
        parsed = JSON.parse(holder.textContent);
      } catch (error) {
        parsed = {};
      }
    }

    dialog.__zSizeStrings = parsed;
    return parsed;
  };

  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
    );

  /* ---------------------------------------------------------------- units */

  const setUnit = (dialog, unit, options = {}) => {
    const selectedUnit = unit === 'in' ? 'in' : 'cm';
    const previousUnit = getUnit(dialog);
    dialog.dataset.unit = selectedUnit;

    dialog.querySelectorAll('[data-z-size-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.zSizeUnit === selectedUnit ? 'true' : 'false');
    });

    dialog.querySelectorAll('[data-z-measurement][data-cm]').forEach((measurement) => {
      const values = measurement.dataset.cm.split('|');
      const displayValues = values.map((value) => formatMeasurement(value, selectedUnit));
      measurement.textContent = `${displayValues.join('–')} ${selectedUnit}`;
    });

    dialog.querySelectorAll('[data-z-calc-unit-label]').forEach((label) => {
      label.textContent = selectedUnit;
    });

    if (options.convertInputs !== false && previousUnit !== selectedUnit) {
      dialog.querySelectorAll('[data-z-calc-chest], [data-z-calc-waist]').forEach((input) => {
        const current = toNumber(input.value);
        if (current === null) return;
        const converted = selectedUnit === 'in' ? current / 2.54 : current * 2.54;
        input.value = String(Math.round(converted * 10) / 10);
      });
    }

    if (options.persist !== false) writeStored(STORAGE_UNIT, selectedUnit);
    if (options.rerender !== false) renderStoredResult(dialog);
  };

  /* Visitors shopping in inches should not have to reach for the switch. */
  const detectUnit = () => {
    const country = (window.Shopify && window.Shopify.country) || '';
    if (country) return country === 'US' ? 'in' : 'cm';

    try {
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (/^(America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Indiana|Kentucky|Boise|Juneau|Sitka|Nome|Adak|Menominee|North_Dakota)|Pacific\/Honolulu)/.test(zone)) {
        return 'in';
      }
    } catch (error) {
      /* Fall through to the language check. */
    }

    return (navigator.language || '').toLowerCase() === 'en-us' ? 'in' : 'cm';
  };

  /* ----------------------------------------------------------------- tabs */

  const activateTab = (dialog, tabName, moveFocus = false) => {
    const tabs = Array.from(dialog.querySelectorAll('[data-z-size-tab]')).filter((tab) => !tab.hidden);
    const selectedTab = tabs.find((tab) => tab.dataset.zSizeTab === tabName) || tabs[0];
    if (!selectedTab) return;

    tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;
    });

    dialog.querySelectorAll('[data-z-size-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.zSizePanel !== selectedTab.dataset.zSizeTab;
    });

    const panelScroller = dialog.querySelector('.z-size-guide__body');
    if (panelScroller) panelScroller.scrollTop = 0;

    if (moveFocus) selectedTab.focus();
  };

  const handleTabKeys = (dialog, event) => {
    const currentTab = event.target.closest('[data-z-size-tab]');
    if (!currentTab) return;

    const tabs = Array.from(dialog.querySelectorAll('[data-z-size-tab]')).filter((tab) => !tab.hidden);
    const currentIndex = tabs.indexOf(currentTab);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;

    event.preventDefault();
    activateTab(dialog, tabs[nextIndex].dataset.zSizeTab, true);
  };

  const trapFocus = (dialog, event) => {
    if (event.key !== 'Tab') return;
    const focusableElements = getFocusableElements(dialog);
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ----------------------------------------------------------- chart data */

  const readRows = (dialog) => {
    const bodyRows = Array.from(dialog.querySelectorAll('[data-z-size-table="body"] tr[data-z-size-row]'));
    const garmentRows = Array.from(dialog.querySelectorAll('[data-z-size-table="garment"] tr[data-z-size-row]'));

    const garmentBySize = new Map();
    garmentRows.forEach((row) => {
      garmentBySize.set(row.dataset.size, {
        chest: toNumber(row.dataset.garmentChest),
        waist: toNumber(row.dataset.garmentWaist),
        bottom: toNumber(row.dataset.garmentBottom)
      });
    });

    const orNull = (value) => (value === null || value === undefined ? null : value);

    return bodyRows
      .map((row) => {
        const garment = garmentBySize.get(row.dataset.size) || {};
        const chest = toNumber(row.dataset.bodyChest);
        const waist = toNumber(row.dataset.bodyWaist);

        /* What the finished jacket measures. The garment chart is the source of
           truth; where a row carries none, fall back to the body chart plus the
           standard ease. */
        const garmentChest = orNull(garment.chest);
        const garmentWaist = orNull(garment.waist);

        return {
          size: row.dataset.size || '',
          eu: row.dataset.eu || '',
          us: row.dataset.us || '',
          chest,
          waist,
          fitChest: garmentChest !== null ? garmentChest : chest === null ? null : chest + CHEST_EASE,
          fitWaist: garmentWaist !== null ? garmentWaist : waist === null ? null : waist + WAIST_EASE,
          garment
        };
      })
      .filter((row) => row.fitChest !== null)
      .sort((a, b) => a.fitChest - b.fitChest);
  };

  const sizeLabel = (dialog, row) => {
    const strings = getStrings(dialog);
    const parts = [row.size];
    if (row.us) parts.push(`${strings.labelUs} ${row.us}`);
    if (row.eu) parts.push(`${strings.labelEu} ${row.eu}`);
    return parts.join(' · ');
  };

  /* ----------------------------------------------------- variant handling */

  const normalizeSize = (value) =>
    String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');

  const sizeAliases = (size) => {
    const key = normalizeSize(size);
    const aliases = {
      XXL: ['XXL', '2XL'],
      '2XL': ['2XL', 'XXL'],
      XXXL: ['XXXL', '3XL'],
      '3XL': ['3XL', 'XXXL'],
      XXXXL: ['XXXXL', '4XL'],
      '4XL': ['4XL', 'XXXXL']
    };
    return aliases[key] || [key];
  };

  const findSizeControl = (dialog, size) => {
    const scope = dialog.closest('variant-selects');
    if (!scope) return null;

    const radios = Array.from(scope.querySelectorAll('input[type="radio"]'));
    const options = Array.from(scope.querySelectorAll('select.select__select option'));

    for (const alias of sizeAliases(size)) {
      const radio = radios.find((input) => normalizeSize(input.value) === alias);
      if (radio) {
        return { element: radio, kind: 'radio', available: !radio.classList.contains('disabled') };
      }

      const option = options.find((item) => normalizeSize(item.value) === alias);
      if (option) {
        return { element: option, kind: 'option', available: !option.disabled };
      }
    }

    return null;
  };

  const applySize = (dialog, size) => {
    const control = findSizeControl(dialog, size);
    if (!control || !control.available) return false;

    if (control.kind === 'radio') {
      control.element.checked = true;
      control.element.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const select = control.element.closest('select');
      if (!select) return false;
      select.value = control.element.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return true;
  };

  /* ----------------------------------------------------------- calculator */

  /* Nearest size by absolute difference; a tie goes to the larger size, which is
     also what the fit advice on the chart recommends. */
  const pickIndex = (rows, key, target) => {
    let bestIndex = -1;
    let bestDistance = Infinity;

    rows.forEach((row, index) => {
      const nominal = row[key];
      if (nominal === null || nominal === undefined) return;
      const distance = Math.abs(nominal - target);
      if (distance <= bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex === -1 ? rows.length - 1 : bestIndex;
  };

  const calculate = (rows, { chestCm, waistCm }) => {
    /* Ease is added to the body measurement, then the first size whose finished
       chest AND finished waist both clear those targets is the recommendation.
       Nothing below that point fits, so there is no rounding down. */
    const targetChest = chestCm + CHEST_EASE;
    const targetWaist = waistCm === null ? null : waistCm + WAIST_EASE;

    const clearsChest = (row) => row.fitChest !== null && row.fitChest >= targetChest - EPSILON;
    const clearsWaist = (row) =>
      targetWaist === null || row.fitWaist === null || row.fitWaist >= targetWaist - EPSILON;

    const index = rows.findIndex((row) => clearsChest(row) && clearsWaist(row));

    /* Past the largest pattern there is nothing to sell: say so rather than
       pushing the biggest size at someone it will not close on. */
    if (index === -1) {
      return { row: null, customSize: true, targetChest, targetWaist };
    }

    /* Whether the waist, not the chest, is what moved the recommendation up. */
    const chestOnlyIndex = rows.findIndex(clearsChest);
    const row = rows[index];

    return {
      row,
      customSize: false,
      targetChest,
      targetWaist,
      index,
      chestRoom: row.fitChest === null ? null : row.fitChest - chestCm,
      waistRoom: row.fitWaist === null || waistCm === null ? null : row.fitWaist - waistCm,
      waistDrivesUp: chestOnlyIndex !== -1 && index > chestOnlyIndex,
      belowRange: targetChest < rows[0].fitChest - 2
    };
  };

  const renderResult = (dialog, state) => {
    const container = dialog.querySelector('[data-z-calc-result]');
    if (!container) return;

    const rows = readRows(dialog);
    if (!rows.length) return;

    const strings = getStrings(dialog);
    const unit = getUnit(dialog);
    const outcome = calculate(rows, state);
    const show = (value) => formatNumber(Math.round(value * 10) / 10, unit);

    const parts = [];

    if (outcome.customSize) {
      parts.push('<div class="z-size-guide__result-head">');
      parts.push(`<span class="z-size-guide__section-label">${strings.calculatorLabel || ''}</span>`);
      parts.push(`<strong class="z-size-guide__result-size">${strings.customSize || ''}</strong>`);
      parts.push('</div>');
      parts.push('<div class="z-size-guide__result-body">');
      parts.push(`<p class="z-size-guide__result-copy">${strings.customSizeText || ''}</p>`);
      parts.push('</div>');

      container.innerHTML = parts.join('');
      container.hidden = false;
      return;
    }

    const row = outcome.row;

    const specs = [
      { label: strings.specChest, value: row.garment.chest },
      { label: strings.specWaist, value: row.garment.waist },
      { label: strings.specBottom, value: row.garment.bottom }
    ].filter((spec) => spec.value !== null && spec.value !== undefined);

    const control = findSizeControl(dialog, row.size);
    const canSelect = Boolean(control && control.available);

    parts.push('<div class="z-size-guide__result-head">');
    parts.push(`<span class="z-size-guide__section-label">${strings.recommended || ''}</span>`);
    parts.push(`<strong class="z-size-guide__result-size">${sizeLabel(dialog, row)}</strong>`);
    parts.push('</div>');

    parts.push('<div class="z-size-guide__result-body">');
    parts.push(
      `<p class="z-size-guide__result-copy">${fill(strings.resultCopy, { size: row.size })}</p>`
    );

    /* Spell the arithmetic out: body measurement, the room we add, and what the
       jacket actually measures. */
    if (row.fitChest !== null) {
      parts.push(
        `<p class="z-size-guide__result-room">${fill(strings.resultRoom, {
          chest: show(state.chestCm),
          chest_ease: show(CHEST_EASE),
          target_chest: show(outcome.targetChest),
          garment_chest: show(row.fitChest),
          unit
        })}</p>`
      );
    }

    if (state.waistCm !== null && row.fitWaist !== null) {
      parts.push(
        `<p class="z-size-guide__result-room">${fill(strings.resultWaist, {
          waist: show(state.waistCm),
          waist_ease: show(WAIST_EASE),
          target_waist: show(outcome.targetWaist),
          garment_waist: show(row.fitWaist),
          unit
        })}</p>`
      );
    }

    if (outcome.waistDrivesUp) {
      parts.push(`<p class="z-size-guide__result-note">${strings.noteWaist}</p>`);
    }

    if (outcome.belowRange) {
      parts.push(
        `<p class="z-size-guide__result-note">${fill(strings.noteBelow, { size: row.size })}</p>`
      );
    }

    if (specs.length) {
      parts.push('<dl class="z-size-guide__result-specs">');
      specs.forEach((spec) => {
        parts.push(
          `<div><dt>${spec.label}</dt><dd>${formatNumber(spec.value, unit)} ${unit}</dd></div>`
        );
      });
      parts.push('</dl>');
    }

    if (canSelect) {
      parts.push(
        `<button class="z-size-guide__result-cta" type="button" data-z-calc-select="${row.size}">${fill(
          strings.selectSize,
          { size: row.size }
        )}</button>`
      );
    } else if (control) {
      parts.push(
        `<p class="z-size-guide__result-unavailable">${fill(strings.sizeUnavailable, { size: row.size })}</p>`
      );
    }

    parts.push('</div>');

    container.innerHTML = parts.join('');
    container.hidden = false;
  };

  const renderStoredResult = (dialog) => {
    const state = calcState.get(dialog);
    if (state) renderResult(dialog, state);
  };

  const showCalcError = (dialog, message) => {
    const error = dialog.querySelector('[data-z-calc-error]');
    const result = dialog.querySelector('[data-z-calc-result]');
    if (result) result.hidden = true;
    calcState.delete(dialog);
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const runCalculator = (dialog) => {
    const strings = getStrings(dialog);
    const unit = getUnit(dialog);
    const rows = readRows(dialog);
    if (!rows.length) return;

    const chestInput = dialog.querySelector('[data-z-calc-chest]');
    const waistInput = dialog.querySelector('[data-z-calc-waist]');

    const chestRaw = toNumber(chestInput && chestInput.value);
    if (chestRaw === null) {
      showCalcError(dialog, strings.errorChest);
      if (chestInput) chestInput.focus();
      return;
    }

    const toCm = (value) => (unit === 'in' ? value * 2.54 : value);
    const chestCm = toCm(chestRaw);

    if (chestCm < MIN_CM || chestCm > MAX_CM) {
      showCalcError(
        dialog,
        fill(strings.errorRange, {
          min: formatNumber(MIN_CM, unit),
          max: formatNumber(MAX_CM, unit),
          unit
        })
      );
      if (chestInput) chestInput.focus();
      return;
    }

    const waistRaw = toNumber(waistInput && waistInput.value);
    const waistCm = waistRaw === null ? null : toCm(waistRaw);

    showCalcError(dialog, '');

    const state = {
      chestCm,
      waistCm: waistCm !== null && waistCm >= MIN_CM - 15 && waistCm <= MAX_CM ? waistCm : null
    };

    calcState.set(dialog, state);
    renderResult(dialog, state);

    const result = dialog.querySelector('[data-z-calc-result]');
    if (result) result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const resetCalculator = (dialog) => {
    calcState.delete(dialog);
    const result = dialog.querySelector('[data-z-calc-result]');
    const error = dialog.querySelector('[data-z-calc-error]');
    if (result) {
      result.hidden = true;
      result.innerHTML = '';
    }
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
  };

  /* --------------------------------------------------------------- dialog */

  const prepareDialog = (dialog) => {
    if (dialog.dataset.zSizeGuideReady === 'true') return;
    dialog.dataset.zSizeGuideReady = 'true';

    /* Hide the calculator when the chart carries no usable body measurements. */
    if (!readRows(dialog).length) {
      const tab = dialog.querySelector('[data-z-size-tab="calculator"]');
      const panel = dialog.querySelector('[data-z-size-panel="calculator"]');
      const crossLink = dialog.querySelector('[data-z-size-goto="calculator"]');
      if (tab) tab.hidden = true;
      if (panel) panel.remove();
      if (crossLink) crossLink.hidden = true;
    }

    dialog.addEventListener('close', () => restorePage(dialog));

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeGuide(dialog);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeGuide(dialog);
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeGuide(dialog);
        return;
      }

      handleTabKeys(dialog, event);
      trapFocus(dialog, event);
    });

    const form = dialog.querySelector('[data-z-size-calc]');
    if (form) {
      /* The dialog is rendered inside <variant-selects>, which reacts to any
         change event by re-rendering the whole product block — that would tear
         the open size guide out of the page. Leaving the chest field after
         typing fires exactly such a change event, so the calculator keeps its
         own input, change and submit events to itself. */
      ['input', 'change'].forEach((type) => {
        form.addEventListener(type, (event) => event.stopPropagation());
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        runCalculator(dialog);
      });

      form.addEventListener('reset', () => {
        window.setTimeout(() => resetCalculator(dialog), 0);
      });

      form.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input[type="number"]')) {
          event.preventDefault();
          runCalculator(dialog);
        }
      });
    }
  };

  const openGuide = (button) => {
    const dialog = document.getElementById(button.getAttribute('aria-controls'));
    if (!dialog || dialog.hasAttribute('open')) return;

    prepareDialog(dialog);
    returnFocus.set(dialog, button);
    button.setAttribute('aria-expanded', 'true');
    activateTab(dialog, 'body');

    const storedUnit = readStored(STORAGE_UNIT);
    const unit = storedUnit === 'in' || storedUnit === 'cm' ? storedUnit : detectUnit();
    setUnit(dialog, unit, { persist: false, convertInputs: false, rerender: false });

    renderStoredResult(dialog);

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    document.documentElement.classList.add('z-size-guide-open');
    requestAnimationFrame(() => {
      const activeTab = dialog.querySelector('[data-z-size-tab][aria-selected="true"]');
      activeTab?.focus({ preventScroll: true });
    });
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-z-size-guide-open]');
    if (openButton) {
      event.preventDefault();
      openGuide(openButton);
      return;
    }

    const closeButton = event.target.closest('[data-z-size-guide-close]');
    if (closeButton) {
      event.preventDefault();
      closeGuide(closeButton.closest('[data-z-size-guide]'));
      return;
    }

    const selectButton = event.target.closest('[data-z-calc-select]');
    if (selectButton) {
      event.preventDefault();
      const dialog = selectButton.closest('[data-z-size-guide]');
      if (applySize(dialog, selectButton.dataset.zCalcSelect)) closeGuide(dialog);
      return;
    }

    const goto = event.target.closest('[data-z-size-goto]');
    if (goto) {
      event.preventDefault();
      activateTab(goto.closest('[data-z-size-guide]'), goto.dataset.zSizeGoto, true);
      return;
    }

    const tab = event.target.closest('[data-z-size-tab]');
    if (tab) {
      activateTab(tab.closest('[data-z-size-guide]'), tab.dataset.zSizeTab);
      return;
    }

    const unitButton = event.target.closest('[data-z-size-unit]');
    if (unitButton) {
      setUnit(unitButton.closest('[data-z-size-guide]'), unitButton.dataset.zSizeUnit);
    }
  });
})();
